import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import Swal from 'sweetalert2';
import UserNav from '../../components/navbar/user-nav';
import FooterTop from '../../components/footer-top';
import Footer from '../../components/footer';
import Breadcrumb from '../../components/Breadcrumb';
import ImageGallery from '../../components/ImageGallery';

export default function VerPropiedadAdmin() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();

  const [propiedad, setPropiedad] = useState<any>(null);
  const [inmobiliaria, setInmobiliaria] = useState<any>(null);
  const [asesores, setAsesores] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Para asignar agente
  const [mostrarAsignar, setMostrarAsignar] = useState(false);
  const [agentesDisponibles, setAgentesDisponibles] = useState<any[]>([]);
  const [busquedaAgente, setBusquedaAgente] = useState('');
  const [cargandoAgentes, setCargandoAgentes] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) { setLoading(false); return; }

      try {
        const propSnap = await getDoc(doc(db, 'propiedades', id));
        if (!propSnap.exists()) {
          Swal.fire({ icon: 'error', title: 'No encontrada', text: 'La propiedad no existe.' });
          return navigate('/admin-property');
        }

        const propData = propSnap.data();
        setPropiedad({ docId: propSnap.id, ...propData });

        // Permisos
        const esAdmin = user?.tipo?.includes('admin');
        const esAsociado = user?.tipo?.includes('asociado') && propData.inmobiliaria_doc_id === user.inmobiliaria_doc_id;
        if (!esAdmin && !esAsociado) {
          Swal.fire({ icon: 'error', title: 'Acceso denegado', text: 'No tienes permiso.' });
          return navigate('/admin-property');
        }

        // Inmobiliaria
        const inmId = propData.inmobiliaria_doc_id || propData.inmobiliaria_id;
        if (inmId) {
          try {
            const inmoSnap = await getDoc(doc(db, 'inmobiliaria', inmId));
            if (inmoSnap.exists()) setInmobiliaria({ id: inmoSnap.id, ...inmoSnap.data() });
          } catch {}
        }

        // Asesores
        if (propData.asesor_doc_ids && Array.isArray(propData.asesor_doc_ids)) {
          const asesoresData: any[] = [];
          for (const docId of propData.asesor_doc_ids) {
            try {
              const asesorSnap = await getDoc(doc(db, 'asesor', docId));
              if (asesorSnap.exists()) asesoresData.push({ id: asesorSnap.id, ...asesorSnap.data() });
            } catch {}
          }
          setAsesores(asesoresData);
        }
      } catch (error) {
        console.error('Error cargando datos:', error);
        Swal.fire('Error', 'No se pudo cargar la propiedad.', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, user, navigate]);

  const cargarAgentesDisponibles = async () => {
    setCargandoAgentes(true);
    try {
      const snap = await getDocs(query(collection(db, 'asesor'), where('activo', '==', true)));
      const agentes = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Excluir los ya asignados
      const asignadosIds = propiedad?.asesor_doc_ids || [];
      setAgentesDisponibles(agentes.filter((a: any) => !asignadosIds.includes(a.id)));
    } catch (error) {
      console.error('Error cargando agentes:', error);
    } finally {
      setCargandoAgentes(false);
    }
  };

  const asignarAgente = async (agenteId: string, agenteNombre: string) => {
    try {
      await updateDoc(doc(db, 'propiedades', id!), {
        asesor_doc_ids: arrayUnion(agenteId)
      });
      // Actualizar tambien el array en el asesor
      await updateDoc(doc(db, 'asesor', agenteId), {
        propiedad_id: arrayUnion(id)
      });

      // Actualizar estado local
      setPropiedad((prev: any) => ({
        ...prev,
        asesor_doc_ids: [...(prev.asesor_doc_ids || []), agenteId]
      }));

      // Recargar asesor info
      const asesorSnap = await getDoc(doc(db, 'asesor', agenteId));
      if (asesorSnap.exists()) {
        setAsesores(prev => [...prev, { id: asesorSnap.id, ...asesorSnap.data() }]);
      }

      setAgentesDisponibles(prev => prev.filter((a: any) => a.id !== agenteId));
      Swal.fire({ icon: 'success', title: 'Asignado', text: `${agenteNombre} fue asignado a esta propiedad.`, timer: 1500, showConfirmButton: false });
    } catch (error) {
      console.error('Error asignando agente:', error);
      Swal.fire('Error', 'No se pudo asignar el agente.', 'error');
    }
  };

  const desasignarAgente = async (agenteId: string, agenteNombre: string) => {
    const confirm = await Swal.fire({
      title: `Desasignar a ${agenteNombre}?`,
      text: 'Se removera de esta propiedad.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Si, desasignar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirm.isConfirmed) return;

    try {
      await updateDoc(doc(db, 'propiedades', id!), {
        asesor_doc_ids: arrayRemove(agenteId)
      });
      await updateDoc(doc(db, 'asesor', agenteId), {
        propiedad_id: arrayRemove(id)
      });

      setPropiedad((prev: any) => ({
        ...prev,
        asesor_doc_ids: (prev.asesor_doc_ids || []).filter((aid: string) => aid !== agenteId)
      }));
      setAsesores(prev => prev.filter(a => a.id !== agenteId));
      Swal.fire({ icon: 'success', title: 'Desasignado', timer: 1500, showConfirmButton: false });
    } catch (error) {
      console.error('Error desasignando agente:', error);
      Swal.fire('Error', 'No se pudo desasignar.', 'error');
    }
  };

  if (loading) {
    return (
      <div className="container mt-5 text-center py-5">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-3">Cargando propiedad...</p>
      </div>
    );
  }

  if (!propiedad) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger text-center">
          <h4>Propiedad no encontrada</h4>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/admin-property')}>Volver</button>
        </div>
      </div>
    );
  }

  const formatPrice = (val?: number, currency = 'USD') => {
    if (!val || val <= 0) return null;
    return new Intl.NumberFormat(currency === 'MXN' ? 'es-MX' : 'en-US', {
      style: 'currency', currency, minimumFractionDigits: 0
    }).format(val);
  };

  const getRetsClassLabel = (c?: string) => {
    const map: Record<string, string> = { A: 'Departamento', B: 'Casa', E: 'Terreno', F: 'Comercial', G: 'Negocio', H: 'Fraccional', I: 'Multi-Familiar' };
    return map[c || ''] || c || '-';
  };

  const formatDate = (ts: any) => {
    if (!ts) return '-';
    const date = ts.toDate ? ts.toDate() : ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
    return date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const nombre = propiedad.titulo || propiedad.nombre || `MLS# ${propiedad.list_number || 'Sin titulo'}`;
  const esMls = propiedad.fuente === 'flexmls';
  const df = propiedad.datos_flex || {};

  const agentesFiltrados = agentesDisponibles.filter((a: any) => {
    if (!busquedaAgente.trim()) return true;
    const q = busquedaAgente.toLowerCase();
    return (a.nombre || '').toLowerCase().includes(q) || (a.correo || '').toLowerCase().includes(q);
  });

  return (
    <>
      <UserNav />
      <div className="container-fluid px-4 mt-4 mb-5">
        <Breadcrumb items={[
          { label: 'Dashboard', path: '/admin-dashboard' },
          { label: 'Propiedades MLS', path: '/admin-property' },
          { label: nombre }
        ]} />

        {/* Header */}
        <div className="row mb-3">
          <div className="col-lg-8">
            <h2 className="mb-1">{nombre}</h2>
            <div className="d-flex gap-2 flex-wrap align-items-center">
              {esMls && (
                <span className="badge bg-primary">
                  <i className="fas fa-database me-1"></i>MLS# {propiedad.list_number || propiedad.listing_id}
                </span>
              )}
              {df.rets_class && (
                <span className="badge bg-secondary">{getRetsClassLabel(df.rets_class)}</span>
              )}
              <span className={`badge ${propiedad.activo !== false ? 'bg-success' : 'bg-danger'}`}>
                {propiedad.activo !== false ? 'Activa' : 'Inactiva'}
              </span>
              {df.mls_status && (
                <span className="badge bg-info">MLS: {df.mls_status}</span>
              )}
              {propiedad.tipo_propiedad && (
                <span className="badge bg-dark">{propiedad.tipo_propiedad}</span>
              )}
            </div>
          </div>
          <div className="col-lg-4 text-lg-end mt-2 mt-lg-0">
            <div className="d-flex gap-2 justify-content-lg-end flex-wrap">
              <button className="btn btn-outline-primary btn-sm" onClick={() => navigate(`/single-property-1/${id}`)}>
                <i className="fas fa-external-link-alt me-1"></i>Ver publica
              </button>
              <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/admin-property')}>
                <i className="fas fa-arrow-left me-1"></i>Volver
              </button>
            </div>
          </div>
        </div>

        {/* Aviso MLS */}
        {esMls && (
          <div className="alert alert-info py-2 d-flex align-items-center" style={{ fontSize: '0.85rem' }}>
            <i className="fas fa-info-circle me-2"></i>
            <span>
              Propiedad sincronizada desde FlexMLS. Los datos se actualizan automaticamente cada 6 horas.
              Solo puedes <strong>asignar/desasignar agentes</strong> locales.
            </span>
          </div>
        )}

        <div className="row">
          {/* Columna izquierda: Datos */}
          <div className="col-lg-8">
            {/* Precio y specs */}
            <div className="card mb-3 shadow-sm">
              <div className="card-body">
                <div className="row">
                  <div className="col-sm-4 mb-3">
                    <small className="text-muted d-block">Precio</small>
                    <h4 className="text-success mb-0">
                      {formatPrice(propiedad.precio_actual_usd) || formatPrice(propiedad.precio_original_usd) || 'No definido'}
                    </h4>
                    {propiedad.precio_original_usd && propiedad.precio_actual_usd !== propiedad.precio_original_usd && (
                      <small className="text-muted text-decoration-line-through">
                        {formatPrice(propiedad.precio_original_usd)}
                      </small>
                    )}
                  </div>
                  <div className="col-sm-8">
                    <div className="row text-center">
                      <div className="col-3">
                        <small className="text-muted d-block">Recamaras</small>
                        <strong>{propiedad.recamaras || 0}</strong>
                      </div>
                      <div className="col-3">
                        <small className="text-muted d-block">Banos</small>
                        <strong>{propiedad.banos_completos || 0}</strong>
                        {propiedad.medios_banos > 0 && <small className="text-muted"> +{propiedad.medios_banos}m</small>}
                      </div>
                      <div className="col-3">
                        <small className="text-muted d-block">M2 Const</small>
                        <strong>{propiedad.m2_total ? Math.round(propiedad.m2_total) : '-'}</strong>
                      </div>
                      <div className="col-3">
                        <small className="text-muted d-block">M2 Lote</small>
                        <strong>{propiedad.m2_lote ? Math.round(propiedad.m2_lote) : '-'}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Ubicacion */}
            <div className="card mb-3 shadow-sm">
              <div className="card-header bg-light py-2">
                <strong><i className="fas fa-map-marker-alt me-2"></i>Ubicacion</strong>
              </div>
              <div className="card-body py-2">
                <div className="row">
                  <div className="col-sm-4 mb-2">
                    <small className="text-muted d-block">Zona</small>
                    <span>{propiedad.zona || propiedad.direccion?.zona || '-'}</span>
                  </div>
                  <div className="col-sm-4 mb-2">
                    <small className="text-muted d-block">Comunidad</small>
                    <span>{propiedad.comunidad || propiedad.direccion?.comunidad || '-'}</span>
                  </div>
                  <div className="col-sm-4 mb-2">
                    <small className="text-muted d-block">Ciudad</small>
                    <span>{propiedad.ciudad || propiedad.direccion?.ciudad || '-'}</span>
                  </div>
                  {propiedad.direccion?.calle && (
                    <div className="col-sm-6 mb-2">
                      <small className="text-muted d-block">Calle</small>
                      <span>{propiedad.direccion.calle} {propiedad.direccion.numero || ''}</span>
                    </div>
                  )}
                  {propiedad.estilo && (
                    <div className="col-sm-3 mb-2">
                      <small className="text-muted d-block">Estilo</small>
                      <span>{propiedad.estilo}</span>
                    </div>
                  )}
                  {propiedad.anos_construccion > 0 && (
                    <div className="col-sm-3 mb-2">
                      <small className="text-muted d-block">Ano construido</small>
                      <span>{propiedad.anos_construccion}</span>
                    </div>
                  )}
                </div>
                {propiedad.direccion?.lat && propiedad.direccion?.lng && (
                  <a
                    href={`https://www.google.com/maps?q=${propiedad.direccion.lat},${propiedad.direccion.lng}`}
                    target="_blank" rel="noreferrer" className="btn btn-sm btn-outline-primary mt-1"
                  >
                    <i className="fas fa-map me-1"></i>Google Maps
                  </a>
                )}
              </div>
            </div>

            {/* Descripcion */}
            {propiedad.descripcion && (
              <div className="card mb-3 shadow-sm">
                <div className="card-header bg-light py-2">
                  <strong><i className="fas fa-align-left me-2"></i>Descripcion</strong>
                </div>
                <div className="card-body py-2">
                  <p className="mb-0" style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{propiedad.descripcion}</p>
                </div>
              </div>
            )}

            {/* Galeria */}
            <div className="card mb-3 shadow-sm">
              <div className="card-header bg-light py-2">
                <strong><i className="fas fa-images me-2"></i>Fotos ({propiedad.fotos?.length || 0})</strong>
              </div>
              <div className="card-body">
                {propiedad.fotos && propiedad.fotos.length > 0 ? (
                  <ImageGallery
                    images={propiedad.fotos}
                    showThumbnails={true}
                    thumbnailSize="lg"
                    lightbox={true}
                    className="admin-property-gallery"
                  />
                ) : (
                  <p className="text-muted mb-0">Sin fotos disponibles.</p>
                )}
              </div>
            </div>

            {/* Amenidades */}
            {propiedad.amenidades && propiedad.amenidades.length > 0 && (
              <div className="card mb-3 shadow-sm">
                <div className="card-header bg-light py-2">
                  <strong><i className="fas fa-star me-2"></i>Amenidades</strong>
                </div>
                <div className="card-body py-2">
                  <div className="d-flex flex-wrap gap-2">
                    {propiedad.amenidades.map((a: string, i: number) => (
                      <span key={i} className="badge bg-light text-dark border">
                        <i className="fas fa-check text-success me-1"></i>{a}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Columna derecha: Relaciones y datos MLS */}
          <div className="col-lg-4">
            {/* Agencia */}
            <div className="card mb-3 shadow-sm">
              <div className="card-header bg-light py-2">
                <strong><i className="fas fa-building me-2"></i>Agencia</strong>
              </div>
              <div className="card-body py-2">
                {inmobiliaria ? (
                  <>
                    <h6 className="mb-1">{inmobiliaria.nombre}</h6>
                    {inmobiliaria.correo && <small className="text-muted d-block"><i className="fas fa-envelope me-1"></i>{inmobiliaria.correo}</small>}
                    {inmobiliaria.telefono && <small className="text-muted d-block"><i className="fas fa-phone me-1"></i>{inmobiliaria.telefono}</small>}
                    <button className="btn btn-sm btn-outline-primary mt-2" onClick={() => navigate(`/ver-inm/${inmobiliaria.id}`)}>
                      Ver agencia
                    </button>
                  </>
                ) : (
                  <div>
                    <span className="text-danger"><i className="fas fa-exclamation-triangle me-1"></i>Sin agencia asignada</span>
                    {df.list_office_name && (
                      <small className="text-muted d-block mt-1">MLS Office: {df.list_office_name}</small>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Agentes asignados */}
            <div className="card mb-3 shadow-sm">
              <div className="card-header bg-light py-2 d-flex justify-content-between align-items-center">
                <strong><i className="fas fa-users me-2"></i>Agentes ({asesores.length})</strong>
                {user?.tipo?.includes('admin') && (
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => { setMostrarAsignar(!mostrarAsignar); if (!mostrarAsignar) cargarAgentesDisponibles(); }}
                  >
                    <i className={`fas ${mostrarAsignar ? 'fa-times' : 'fa-plus'} me-1`}></i>
                    {mostrarAsignar ? 'Cerrar' : 'Asignar'}
                  </button>
                )}
              </div>
              <div className="card-body py-2">
                {asesores.length > 0 ? (
                  <div className="list-group list-group-flush">
                    {asesores.map(a => (
                      <div key={a.id} className="list-group-item px-0 d-flex justify-content-between align-items-center">
                        <div>
                          <strong style={{ fontSize: '0.9rem' }}>{a.nombre}</strong>
                          <small className="text-muted d-block">{a.correo || 'Sin correo'}</small>
                          {a.verificacion_pendiente && (
                            <span className="badge bg-warning" style={{ fontSize: '0.65rem' }}>Pendiente verificar</span>
                          )}
                        </div>
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(`/ver-admin-afiliado/${a.id}`)} title="Ver perfil">
                            <i className="fas fa-eye"></i>
                          </button>
                          {user?.tipo?.includes('admin') && (
                            <button className="btn btn-sm btn-outline-danger" onClick={() => desasignarAgente(a.id, a.nombre)} title="Desasignar">
                              <i className="fas fa-user-minus"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-2">
                    <i className="fas fa-user-slash text-danger me-1"></i>
                    <span className="text-muted">Sin agentes asignados</span>
                    {df.list_agent_name && (
                      <small className="text-muted d-block mt-1">MLS Agent: {df.list_agent_name}</small>
                    )}
                  </div>
                )}

                {/* Panel de asignar agente */}
                {mostrarAsignar && (
                  <div className="mt-3 border-top pt-3">
                    <input
                      type="text"
                      className="form-control form-control-sm mb-2"
                      placeholder="Buscar agente por nombre o correo..."
                      value={busquedaAgente}
                      onChange={e => setBusquedaAgente(e.target.value)}
                    />
                    {cargandoAgentes ? (
                      <div className="text-center py-2"><div className="spinner-border spinner-border-sm" /></div>
                    ) : (
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {agentesFiltrados.length === 0 ? (
                          <small className="text-muted">No hay agentes disponibles</small>
                        ) : (
                          agentesFiltrados.slice(0, 20).map((a: any) => (
                            <div key={a.id} className="d-flex justify-content-between align-items-center py-1 border-bottom">
                              <div>
                                <small className="fw-semibold">{a.nombre}</small>
                                <br />
                                <small className="text-muted">{a.correo || ''}</small>
                              </div>
                              <button className="btn btn-sm btn-success" onClick={() => asignarAgente(a.id, a.nombre)}>
                                <i className="fas fa-plus"></i>
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Datos MLS */}
            {esMls && (
              <div className="card mb-3 shadow-sm">
                <div className="card-header bg-light py-2">
                  <strong><i className="fas fa-database me-2"></i>Datos MLS</strong>
                </div>
                <div className="card-body py-2" style={{ fontSize: '0.85rem' }}>
                  <table aria-label="Tabla de View Admin Property" className="table table-sm table-borderless mb-0">
                    <tbody>
                      <tr><td className="text-muted">Listing ID</td><td><code>{propiedad.listing_id || '-'}</code></td></tr>
                      <tr><td className="text-muted">Clase RETS</td><td>{df.rets_class} - {getRetsClassLabel(df.rets_class)}</td></tr>
                      <tr><td className="text-muted">Status MLS</td><td><span className="badge bg-info">{df.mls_status || '-'}</span></td></tr>
                      {df.list_agent_name && <tr><td className="text-muted">Agente MLS</td><td>{df.list_agent_name}</td></tr>}
                      {df.list_agent_key && <tr><td className="text-muted">Agent Key</td><td><code>{df.list_agent_key}</code></td></tr>}
                      {df.list_office_name && <tr><td className="text-muted">Oficina MLS</td><td>{df.list_office_name}</td></tr>}
                      {df.list_office_key && <tr><td className="text-muted">Office Key</td><td><code>{df.list_office_key}</code></td></tr>}
                      {df.property_name && <tr><td className="text-muted">Nombre MLS</td><td>{df.property_name}</td></tr>}
                      {df.development_name && <tr><td className="text-muted">Desarrollo</td><td>{df.development_name}</td></tr>}
                      {df.days_on_market > 0 && <tr><td className="text-muted">Dias en mercado</td><td>{df.days_on_market}</td></tr>}
                      {df.modification_timestamp && (
                        <tr><td className="text-muted">Ultima mod. MLS</td><td>{new Date(df.modification_timestamp).toLocaleDateString('es-MX')}</td></tr>
                      )}
                      <tr><td className="text-muted">Fuente</td><td><span className="badge bg-primary">flexmls</span></td></tr>
                      <tr><td className="text-muted">Registrado</td><td>{formatDate(propiedad.fecha_creacion)}</td></tr>
                      <tr><td className="text-muted">Actualizado</td><td>{formatDate(propiedad.fecha_actualizacion)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Extras */}
            <div className="card mb-3 shadow-sm">
              <div className="card-header bg-light py-2">
                <strong><i className="fas fa-info-circle me-2"></i>Otros datos</strong>
              </div>
              <div className="card-body py-2" style={{ fontSize: '0.85rem' }}>
                <table aria-label="Tabla de View Admin Property" className="table table-sm table-borderless mb-0">
                  <tbody>
                    {propiedad.estacionamiento > 0 && <tr><td className="text-muted">Estacionamiento</td><td>{propiedad.estacionamiento} {propiedad.tipo_estacionamiento || ''}</td></tr>}
                    {propiedad.pisos > 0 && <tr><td className="text-muted">Pisos</td><td>{propiedad.pisos}</td></tr>}
                    {propiedad.sqft > 0 && <tr><td className="text-muted">Sqft</td><td>{Math.round(propiedad.sqft).toLocaleString()}</td></tr>}
                    {propiedad.tour_virtual_url && (
                      <tr>
                        <td className="text-muted">Tour virtual</td>
                        <td><a href={propiedad.tour_virtual_url} target="_blank" rel="noreferrer">Ver tour</a></td>
                      </tr>
                    )}
                    <tr><td className="text-muted">Vistas</td><td>{propiedad.vistas || 0}</td></tr>
                    <tr><td className="text-muted">Destacada</td><td>{propiedad.destacado ? 'Si' : 'No'}</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <FooterTop bg="theme-bg" />
      <Footer />
    </>
  );
}
